import {
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ASSET_CONSTANTS } from '../../../../shared/constants/asset-constants';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { NzFormModule } from 'ng-zorro-antd/form';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  MbInputComponent,
  MbSelectComponent,
  ToastService,
} from '../../../common';
import { IRole, RolesService } from '../../../../core/services/roles.service';
import { LocationsService } from '../../../../core/services';
import { editStaffFormSchema } from './edit-staff-form.schema';
import {
  AccountsService,
  IAccountContent,
  IUpdateStaffPayload,
} from '../../../../core/services/accounts.service';

@Component({
  selector: 'app-edit-staff-form',
  standalone: true,
  imports: [
    NzFormModule,
    NzUploadModule,
    NzInputModule,
    NzSelectModule,
    NzDatePickerModule,
    NzButtonModule,
    NzSpinModule,
    ReactiveFormsModule,
    CommonModule,
    MbInputComponent,
    MbSelectComponent,
    FormsModule,
  ],
  templateUrl: './edit-staff-form.component.html',
})
export class EditStaffFormComponent implements OnInit, OnChanges {
  @Input() staffData: IAccountContent | null = null;
  @Input() organizationId!: string;

  private toastService = inject(ToastService);
  private rolesService = inject(RolesService);
  private accountService = inject(AccountsService);
  private locationsService = inject(LocationsService);

  protected editStaffForm: FormGroup;

  protected roles: IRole[] = [];
  protected locationOptions: { value: string; label: string }[] = [];
  protected districtOptions: { value: string; label: string }[] = [];
  protected sectorOptions: { value: string; label: string }[] = [];
  protected cellOptions: { value: string; label: string }[] = [];

  zodErrors: Record<string, string> = {};
  protected readonly ASSET_CONSTANTS = ASSET_CONSTANTS;

  ngOnInit() {
    this.prefillForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['staffData'] && this.staffData) {
      this.prefillForm();
    }
  }

  prefillForm(): void {
    if (this.staffData) {
      this.editStaffForm.patchValue({
        firstName: this.staffData.fullName.split(' ')[0] || '',
        lastName: this.staffData.fullName.split(' ')[1] || '',
        phoneNumber: this.staffData.phoneNumber || '',
        email: this.staffData.email || '',
        trackingLevel: this.staffData.trackingLevel || '',
      });
    }
  }

  constructor(private fb: FormBuilder) {
    this.editStaffForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', Validators.required],
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern(/^\+250\d{9}$/)],
      ],
      role: ['', Validators.required],
      trackingLevel: ['', Validators.required],
      assignedLocations: [[]],
      districtId: [''],
      sectorId: [''],
      cellId: [''],
    });

    this.rolesService.getRoles().subscribe((roles) => {
      this.roles = roles;
    });
    this.getDistricts();

    // Initialize locationOptions with district options by default
    this.locationsService.getDistricts().subscribe((districts) => {
      this.districtOptions = districts.payload.map((district) => ({
        value: district.id,
        label: district.name,
      }));
    });

    // Listen to tracking level changes to update location options
    this.editStaffForm.get('trackingLevel')?.valueChanges.subscribe((value) => {
      this.handleTrackingLevelChange(value);
    });

    // Listen to district changes to update location options for sectors
    this.editStaffForm.get('districtId')?.valueChanges.subscribe((value) => {
      if (
        value &&
        this.editStaffForm.get('trackingLevel')?.value === 'SECTOR'
      ) {
        this.onDistrictChange(value);
      } else if (
        value &&
        this.editStaffForm.get('trackingLevel')?.value === 'DISTRICT'
      ) {
        this.onDistrictChangeForDistricts();
      }
    });

    // Listen to sector changes to update location options for cells
    this.editStaffForm.get('sectorId')?.valueChanges.subscribe((value) => {
      if (value && this.editStaffForm.get('trackingLevel')?.value === 'CELL') {
        this.onSectorChange(value);
      }
    });
  }

  get getRoleOptions(): { value: string; label: string }[] {
    return this.roles.map((role) => ({
      value: role.id,
      label: role.name.replace(/_/g, ' '),
    }));
  }

  protected trackingLevels = [
    { value: 'DISTRICT', label: 'District' },
    { value: 'SECTOR', label: 'Sector' },
    { value: 'CELL', label: 'Cell' },
    { value: 'VILLAGE', label: 'Village' },
  ];

  protected locations = [
    { value: 'DISTRICT', label: 'District' },
    { value: 'SECTOR', label: 'Sector' },
    { value: 'CELL', label: 'Cell' },
    { value: 'VILLAGE', label: 'Village' },
  ];

  // get district options
  getDistricts(): void {
    this.locationsService.getDistricts().subscribe((districts) => {
      // if tracking level is district, add all districts to the options
      this.districtOptions = districts.payload.map((district) => ({
        value: district.id,
        label: district.name,
      }));
    });
  }

  getSectors(districtId: string): void {
    this.sectorOptions = [];
    // clear form for sector and cell
    this.editStaffForm.get('sectorId')?.setValue('');
    this.editStaffForm.get('cellId')?.setValue('');
    this.editStaffForm.get('assignedLocations')?.setValue([]);

    this.locationsService.getSectors(districtId).subscribe((sectors) => {
      if (this.editStaffForm.get('trackingLevel')?.value === 'SECTOR') {
        this.locationOptions = sectors.payload.content.map((sector) => ({
          value: sector.id,
          label: sector.name,
        }));
      } else {
        this.sectorOptions = sectors.payload.content.map((sector) => ({
          value: sector.id,
          label: sector.name,
        }));
      }
    });
  }

  // Method to handle district change and populate location options for sectors
  onDistrictChange(districtId: string): void {
    this.getSectors(districtId);
  }

  // Method to handle district change and populate location options for districts
  onDistrictChangeForDistricts(): void {
    // For district tracking level, we don't need to do anything special
    // The location options are already set to all districts
  }

  getCells(sectorId: string): void {
    this.cellOptions = [];
    this.editStaffForm.get('cellId')?.setValue('');
    this.editStaffForm.get('assignedLocations')?.setValue([]);

    this.locationsService.getCells(sectorId).subscribe((cells) => {
      if (this.editStaffForm.get('trackingLevel')?.value === 'CELL') {
        this.locationOptions = cells.payload.content.map((cell) => ({
          value: cell.id,
          label: cell.name,
        }));
      } else {
        this.cellOptions = cells.payload.content.map((cell) => ({
          value: cell.id,
          label: cell.name,
        }));
      }
    });
  }

  // Method to handle sector change and populate location options for cells
  onSectorChange(sectorId: string): void {
    this.getCells(sectorId);
  }

  handleTrackingLevelChange(event: any): void {
    this.locationOptions = [];
    this.editStaffForm.get('districtId')?.setValue('');
    this.editStaffForm.get('sectorId')?.setValue('');
    this.editStaffForm.get('cellId')?.setValue('');
    this.editStaffForm.get('assignedLocations')?.setValue([]);

    if (event === 'DISTRICT') {
      // For district tracking level, show all districts as options
      this.locationOptions = [...this.districtOptions];
    } else if (event === 'SECTOR') {
      // If sector is selected, we'll populate options when district is selected
      this.locationOptions = [];
    } else if (event === 'CELL') {
      // If cell is selected, we'll populate options when sector is selected
      this.locationOptions = [];
    } else if (event === 'VILLAGE') {
      // If village is selected, we'll populate options when cell is selected
      this.locationOptions = [];
    }
  }

  onSubmit(callback: () => void): void {
    this.zodErrors = {};
    const formValue = {
      firstName: this.editStaffForm.value.firstName,
      lastName: this.editStaffForm.value.lastName,
      email: this.editStaffForm.value.email,
      phoneNumber: this.editStaffForm.value.phoneNumber,
      role: this.editStaffForm.value.role,
      trackingLevel: this.editStaffForm.value.trackingLevel,
      assignedLocations: this.editStaffForm.value.assignedLocations,
    };
    const zodResult = editStaffFormSchema.safeParse(formValue);
    if (zodResult.success) {
      const editPayload: IUpdateStaffPayload = {
        fullName: formValue.firstName + ' ' + formValue.lastName, // Added space between names
        email: formValue.email,
        phoneNumber: formValue.phoneNumber,
        roleIds: formValue.role.split(','),
        trackingLevel: formValue.trackingLevel,
        assignedLocations: formValue.assignedLocations || [],
      };
      if (this.staffData) {
        this.accountService
          .updateAccountById(this.staffData.id, editPayload)
          .subscribe({
            next: () => {
              callback();
            },
            error: (error) => {
              console.error('Error updating staff', error);
              this.toastService.show(
                error.error.message || 'Error updating staff',
                'error',
                2000
              );
            },
          });
      } else {
        console.error('No staff data available for update');
        this.toastService.show(
          'No staff data available for update',
          'error',
          5000
        );
      }
    } else {
      zodResult.error.issues.forEach((err) => {
        if (err.path && err.path.length > 0) {
          this.zodErrors[err.path[0] as string] = err.message;
        }
      });
      this.markFormGroupTouched();
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.editStaffForm.controls).forEach((key) => {
      const control = this.editStaffForm.get(key);
      control?.markAsTouched();
    });
  }

  getFieldError(fieldName: string): string {
    return this.zodErrors[fieldName] || '';
  }

  // Public method to reset the form
  resetForm(): void {
    this.editStaffForm.reset();
    this.zodErrors = {};
  }
}
